import { useEffect, useRef, useState, type TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/articles';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5500);
    return () => clearInterval(timer);
  }, [total]);

  const goTo = (next: number) => setIndex((next + total) % total);

  const onTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const current = heroSlides[index];

  return (
    <section
      className="relative w-full h-[440px] md:h-[560px] overflow-hidden bg-primary-900"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点资讯轮播"
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} 鲁啊鲁专题`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-end pb-14 md:pb-20">
        <div className="w-full max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-line"></i>
            {current.tag}
          </span>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl lg:text-5xl font-bold leading-tight text-white line-clamp-2 animate-fade-up" key={current.id}>
            {current.title}
          </h1>
          <p className="mt-3 md:mt-4 max-w-2xl text-sm md:text-base leading-relaxed text-white/85 line-clamp-2">
            {current.summary}
          </p>
          <div className="mt-6 flex items-center gap-3">
            <Link
              to={`/detail/${current.articleId}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
            >
              阅读全文
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/community"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-white/40 px-5 py-2.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
            >
              进入社区
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-6 right-4 md:right-8 flex items-center gap-3">
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          aria-label="上一张"
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition-colors"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          aria-label="下一张"
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition-colors"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>

      <div className="absolute z-20 bottom-6 left-4 md:left-8 flex items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-8 bg-accent-500' : 'w-3 bg-white/50 hover:bg-white/80'
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}